import React, { useEffect, useContext, useState } from 'react';
import styled from 'styled-components';

import AppStoreSVG from '../../../assets/appstore-button.svg';
import Arrow from '../../../assets/icons/finish-up-arrow.svg';
import InfoIconSVG from '../../../assets/icons/info-gray.svg';
import Number1 from '../../../assets/icons/number-1-orange.svg';
import Number2 from '../../../assets/icons/number-2-orange.svg';
import Number3 from '../../../assets/icons/number-3-orange.svg';
import SuccessSVG from '../../../assets/icons/success-celebration.svg';
import PlayStoreSVG from '../../../assets/play-store-download.svg';
import WebQRCode from '../../../assets/qr-code-web.svg';
import ScanMeSVG from '../../../assets/scan-me.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import { Footer } from 'src/commons/components/Footer';
import Highlighter from 'src/commons/components/Highlighter';
import { ModalWrapper } from 'src/commons/components/ModalWrapper';
import {
  MOBILE_BREAKPOINT,
  SMALL_MOBILE_BREAKPOINT,
  colors,
  PageNames,
  GOOGLE_PLAYSTORE_APP_LINK,
  APPLE_APPSTORE_APP_LINK,
  CARD_BREAKPOINT,
  CK_REVIEW_LINK,
  SHOW_CK_REVIEW_LINK_OPTIMIZELY_KEY,
} from 'src/commons/constants';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import { pushToDataLayer } from 'src/commons/utilities/logging';
import { goToDashaboard } from 'src/commons/utilities/routes';
import {
  isMobileDevice,
  isAndroidUserAgent,
  isIOSUserAgent,
} from 'src/commons/utilities/userAgent';

interface FinishUpStyleProps {
  bold?: boolean;
  displayQR?: boolean;
}

// #region -- Styles ------------------------------------------------------------------------------
const StyledCard = styled(Card)`
  padding: 0.5rem;
  justify-content: flex-start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.625rem;
  }
`;
StyledCard.displayName = 'StyledCard';

const CongratsCard = styled(StyledCard)`
  margin-bottom: 0.75rem;
  min-height: 0;
  height: 9rem;
  padding: 1.25rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 1.125rem 0.625rem 0.75rem;
    height: 11.75rem;
  }
`;
CongratsCard.displayName = 'CongratsCard';

const ReviewCard = styled(StyledCard)`
  margin-top: 0.75rem;
  min-height: 0;
`;
ReviewCard.displayName = 'ReviewCard';

const CongratsBox = styled.div`
  width: 37.5rem;
  height: 6.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(
    180deg,
    rgba(255, 235, 217, 0.24) -22.82%,
    rgba(255, 235, 217, 0.79) 119.42%
  );
  background-repeat: no-repeat;
  display: grid;
  grid-template-columns: 6.25rem 1fr;
  @media (max-width: ${CARD_BREAKPOINT}) {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    grid-template-columns: none;
  }
`;
CongratsBox.displayName = 'CongratsBox';

const CongratsMessageContainer = styled.div`
  justify-self: baseline;
  align-self: center;
`;
CongratsMessageContainer.displayName = 'CongratsMessageContainer';

const SuccessTopBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 2rem;
`;
SuccessTopBox.displayName = 'SuccessTopBox';

const CardMainHeading = styled.p`
  color: ${colors.TEXT_PRIMARY};
  font-size: 1.125rem;
  line-height: 2rem;
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 17.625rem;
  }
`;

const InfoIconButton = styled.button`
  margin-left: 0.25rem;
  cursor: pointer;
`;

const InfoIcon = styled(InfoIconSVG)`
  height: 1rem;
  width: 1rem;
  padding-bottom: 0.125rem;
`;

const StyledSuccessIcon = styled(SuccessSVG)`
  min-height: 2.4375rem;
  min-width: 2.4375rem;
  margin: 1.875rem 0 0.5625rem 2.125rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    align-self: center;
    margin-left: 0;
    margin-top: 1.5rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-top: 1rem;
  }
`;
StyledSuccessIcon.displayName = 'StyledSuccessIcon';

const CongratsText = styled.p`
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 700;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${CARD_BREAKPOINT}) {
    text-align: center;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 0.875rem;
  }
`;
CongratsText.displayName = 'CongratsText';

const MailText = styled.p`
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 400;
  color: ${colors.TEXT_PRIMARY};
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-bottom: 1.6875rem;
    padding: 0 2.8125rem;
    font-size: 0.875rem;
  }
`;
MailText.displayName = 'MailText';

const HeadingHighlighter = styled(Highlighter)`
  display: inline-block;
  padding: 0 0 1rem;
  background-color: #ffe4cb;
  font-weight: 700;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
HeadingHighlighter.displayName = 'HeadingHighlighter';

const HeadingHighlighterMobile = styled(HeadingHighlighter)`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: inline-block;
  }
`;
HeadingHighlighterMobile.displayName = 'HeadingHighlighterMobile';

const NextStepsArrow = styled(Arrow)<FinishUpStyleProps>`
  position: absolute;
  top: 13.5rem;
  left: 7.1875rem;
  ${(p: FinishUpStyleProps) =>
    !p.displayQR &&
    `
    display: none;
  `}
  @media (max-width: ${CARD_BREAKPOINT}) {
    display: none;
  }
`;
NextStepsArrow.displayName = 'NextStepsArrow';

const QRContainer = styled.div`
  width: 8.625rem;
  margin: 1.875rem 0 1.5625rem;
  border-radius: 0.5rem;
  padding: 0.375rem 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0.75rem 2.5rem rgba(80, 38, 0, 0.1);
  background-color: #ffffff;
`;
QRContainer.displayName = 'QRContainer';

const StyledQRCode = styled(WebQRCode)`
  min-height: 7.8125rem;
  min-width: 7.8125rem;
  max-height: 7.8125rem;
  max-width: 7.8125rem;
`;
StyledQRCode.displayName = 'StyledQRCode';

const StyledScanMe = styled(ScanMeSVG)`
  margin: -0.5rem 0 0.9375rem;
`;
StyledScanMe.displayName = 'StyledScanMe';

const CreditCardAppLink = styled.a`
  margin: 1.5rem 0 1.625rem;
  border-radius: 0.5625rem;
  width: 14.375rem;
  text-align: center;
  padding: 1.5625rem 0;
  box-shadow: 0 0.875rem 2.75rem rgba(80, 38, 0, 0.1);
  background-color: #ffffff;
`;
CreditCardAppLink.displayName = 'CreditCardAppLink';

const SignInContainer = styled.div`
  text-align: center;
  margin-bottom: 2.1875rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    margin-bottom: 1rem;
  }
`;
SignInContainer.displayName = 'SignInContainer';

const SignInQuestion = styled.p`
  color: ${colors.TEXT_PRIMARY};
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
SignInQuestion.displayName = 'SignInQuestion';

const SignInLink = styled.a`
  color: ${colors.TEXT_ANCHOR};
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
SignInLink.displayName = 'SignInLink';

const InstructionContainer = styled.div`
  max-width: 29.5625rem;
  margin: 0.8125rem 0.375rem 3rem;
`;
InstructionContainer.displayName = 'InstructionContainer';

const Instruction = styled.div`
  height: 1.9375rem;
  margin: 0 0 1.375rem;
  display: flex;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: unset;
  }
`;
Instruction.displayName = 'Instruction';

const StyledNumber1 = styled(Number1)`
  min-width: 2.375rem;
`;
StyledNumber1.displayName = 'StyledNumber1';

const StyledNumber2 = styled(Number2)`
  min-width: 2.375rem;
`;
StyledNumber2.displayName = 'StyledNumber2';

const StyledNumber3 = styled(Number3)`
  min-width: 2.375rem;
`;
StyledNumber3.displayName = 'StyledNumber3';

const Text = styled.p`
  margin: 0 0 0 1.125rem;
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 20.1875rem;
  }
`;
Text.displayName = 'Text';

const MobileText = styled(Text)`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: unset;
    display: block;
  }
`;
MobileText.displayName = 'MobileText';

const DesktopText = styled(Text)`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
DesktopText.displayName = 'DesktopText';

const SeparatorContainer = styled.div`
  width: 100%;
  margin-left: 1rem;
  max-width: 28.75rem;
  display: flex;
  flex-direction: row;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: unset;
    padding: 0 0.625rem;
  }
`;
SeparatorContainer.displayName = 'SeparatorContainer';

const Border = styled.div`
  width: 100%;
  margin: 0.375rem 0;
  border-bottom: 0.0625rem solid ${colors.BORDER_SECONDARY};
`;
Border.displayName = 'Border';

const InstantAccessMessage = styled.p`
  margin: 1.875rem 0 3.375rem;
  max-width: 27.5rem;
  font-size: 1.125rem;
  line-height: 2rem;
  font-weight: 400;
  color: ${colors.TEXT_PRIMARY};
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0.625rem;
  }
`;
InstantAccessMessage.displayName = 'InstantAccessMessage';

const ReviewMessage = styled.p`
  font-size: 1.5rem;
  line-height: 2rem;
  margin-top: 2.5rem;
  color: ${colors.TEXT_PRIMARY};
  text-align: center;
  font-weight: 400;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1.3125rem;
    margin-top: 2.375rem;
  }
`;
ReviewMessage.displayName = 'ReviewMessage';

const ReviewLinkContainer = styled.div`
  height: 3.5rem;
  width: 100%;
  max-width: 25.625rem;
  margin: 1.3125rem 0 2.6875rem;
  display: flex;
  justify-content: space-around;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1rem 0 2.1875rem;
    padding: 0 1.3125rem;
  }
  @media (max-width: ${SMALL_MOBILE_BREAKPOINT}) {
    margin: 2.125rem 0 2.1875rem;
    padding: 0 0.5rem;
  }
`;
ReviewLinkContainer.displayName = 'ReviewLinkContainer';

const ReviewLink = styled.a`
  width: 88%;
  border: 0.0625rem solid ${colors.UI_PRIMARY};
  border-radius: 2rem;
  padding: 1.0625rem 0;
  font-size: 1.16875rem;
  font-weight: 700;
  text-decoration: none;
  text-align: center;
  color: ${colors.UI_PRIMARY_ACTION};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`;
ReviewLink.displayName = 'ReviewLink';

const Sup = styled.sup`
  position: relative;
  top: 0.125rem;
  padding-left: 0.0625rem;
  font-size: 0.625rem;
`;
Sup.displayName = 'Sup';

const CreditLineTooltipContent = styled.div`
  width: 21rem;
  max-width: 100%;
  padding-bottom: 1rem;
`;

const CreditLineTooltipText = styled.p`
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  line-height: 1.5rem;
  margin-top: 1rem;
`;
// #endregion

export const FinishUp = () => {
  // Context --------------------------------------------------------------------------------------
  const {
    fetchAndResumeCreditCardApplication,
    fetchingApplication,
    showApplicationFetchError,
    creditCardApplication,
  } = useContext(CreditCardApplicationContext);

  const { applicationAttribution, offerCheck } = creditCardApplication || {};
  const { offer } = offerCheck || {};
  const { creditLimit, creditApr, conditionalOnCashflowVerification } =
    offer || {};
  const isMobile = isMobileDevice();
  const isAndroid = isAndroidUserAgent();
  const isIOS = isIOSUserAgent();
  const showQR = !isAndroid && !isIOS;
  const stepOne = showQR ? ' by scanning the code' : '';

  const [displayReviewMessage, setDisplayReviewMessage] = useState(false);
  const [showCreditLineTooltip, setShowCreditLineTooltip] = useState(false);

  const renderAppLink = () => {
    // Android
    if (isMobile && isAndroid) {
      return (
        <CreditCardAppLink
          id="android-play-store-link"
          href={GOOGLE_PLAYSTORE_APP_LINK}
          target="_blank"
          aria-label="View the app in the Play Store"
        >
          <PlayStoreSVG id="play-store-button" />
        </CreditCardAppLink>
      );
    }
    // iOS
    if (isMobile && isIOS) {
      return (
        <CreditCardAppLink
          id="ios-app-store-link"
          href={APPLE_APPSTORE_APP_LINK}
          target="_blank"
          aria-label="View the app in the App Store"
        >
          <AppStoreSVG id="app-store-button" />
        </CreditCardAppLink>
      );
    }
    // Fallback to QR code shown for desktop
    return (
      <QRContainer>
        <StyledQRCode id="qr-code" />
        <StyledScanMe id="scan-code" />
      </QRContainer>
    );
  };

  // Hooks ----------------------------------------------------------------------------------------
  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.FINISH);
  }, []);

  useEffect(() => {
    if (
      applicationAttribution &&
      typeof conditionalOnCashflowVerification === 'boolean'
    ) {
      const ffEnabled = ifFeatureFlagOn(SHOW_CK_REVIEW_LINK_OPTIMIZELY_KEY);
      const appRefCode = applicationAttribution.campaignRefCode.toLowerCase();
      // Display 'Leave a review' message when in one-step and user came from CK Lightbox
      setDisplayReviewMessage(
        ffEnabled &&
          !conditionalOnCashflowVerification &&
          (appRefCode === 'ck-lbex-cc' || appRefCode === 'ck-lbos-cc')
      );
    }
  }, [applicationAttribution, conditionalOnCashflowVerification]);

  // Push credit_limit to dataLayer for Google Analytics 4 tagging
  useEffect(() => {
    if (creditLimit) pushToDataLayer({ credit_limit: creditLimit });
  }, [creditLimit]);

  // Push rate to dataLayer for Google Analytics 4 tagging
  useEffect(() => {
    if (creditApr) pushToDataLayer({ rate: creditApr });
  }, [creditApr]);

  if (fetchingApplication) {
    return (
      <>
        <LoadingCard />
        <Footer />
      </>
    );
  }

  if (showApplicationFetchError) {
    return (
      <>
        <ErrorCard />
        <Footer />
      </>
    );
  }

  return (
    <>
      <ModalWrapper
        id="credit-line-modal"
        showModal={showCreditLineTooltip}
        setShowModal={setShowCreditLineTooltip}
        marginBottom="6rem"
      >
        <CreditLineTooltipContent>
          <CreditLineTooltipText>
            Other credit cards make you wait to get your money until your
            physical card arrives. We’ll give you immediate access to half of
            your credit limit now and the rest when you receive your card.
          </CreditLineTooltipText>
        </CreditLineTooltipContent>
      </ModalWrapper>
      <CongratsCard>
        <CongratsBox>
          <StyledSuccessIcon id="success-icon" />
          <CongratsMessageContainer>
            <CongratsText id="success-text">
              Congrats! Your card is on the way!
            </CongratsText>
            <MailText id="success-sub-text">
              It should arrive in your mailbox in the next 7-10 business days
            </MailText>
          </CongratsMessageContainer>
        </CongratsBox>
      </CongratsCard>
      <StyledCard>
        <NextStepsArrow displayQR={showQR} />
        <SuccessTopBox>
          <CardMainHeading id="card-main-heading">
            Can’t wait for the mail?
          </CardMainHeading>
          <CardMainHeading id="sign-in-message">
            Sign in to access&nbsp;
            <HeadingHighlighter>50% of your credit line</HeadingHighlighter>
            <HeadingHighlighterMobile>
              50% of your&nbsp;
            </HeadingHighlighterMobile>
            <HeadingHighlighterMobile>credit line</HeadingHighlighterMobile>
            &nbsp;now
            <InfoIconButton
              id="credit-line-tooltip-button"
              type="button"
              onClick={() => setShowCreditLineTooltip(true)}
            >
              <InfoIcon />
            </InfoIconButton>
          </CardMainHeading>
          {renderAppLink()}
        </SuccessTopBox>
        <SignInContainer>
          <SignInQuestion>Don’t want the app?&nbsp;</SignInQuestion>
          <SignInLink
            id="web_sign_in_button"
            href={goToDashaboard()}
            target="_blank"
            aria-label="Sign in to your Fiserv account on the web"
          >
            Sign in on the web
          </SignInLink>
        </SignInContainer>
        <InstructionContainer>
          <Instruction id="instruction-1">
            <StyledNumber1 />
            <DesktopText>
              Download the Prosper
              <Sup>®</Sup> Card app
              {stepOne}
            </DesktopText>
            <MobileText>
              Download the Prosper
              <Sup>®</Sup> Card app
            </MobileText>
          </Instruction>
          <Instruction id="instruction-2">
            <StyledNumber2 />
            <Text>Tap ‘New User’ and sign in with your email address</Text>
          </Instruction>
          <Instruction id="instruction-3">
            <StyledNumber3 />
            <Text>Add your card to your digital wallet</Text>
          </Instruction>
        </InstructionContainer>
      </StyledCard>
      {displayReviewMessage && (
        <ReviewCard>
          <ReviewMessage>How was your application experience?</ReviewMessage>
          <ReviewLinkContainer>
            <ReviewLink
              id="leave-review-button"
              href={CK_REVIEW_LINK}
              target="_blank"
              aria-label="Leave a review for your application experience"
            >
              Leave a review
            </ReviewLink>
          </ReviewLinkContainer>
        </ReviewCard>
      )}
    </>
  );
};
